import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { AdvertisingMaterial, LearningMaterial, LogistikProduct } from '@chancenwerk/db/LogistikProduct.js';
import { LogistikLearningMaterialCategory } from '@chancenwerk/db/enums/LogistikEnums.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-link.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { distinctUntilChanged, fromEvent, takeUntil } from 'rxjs';
import { g_getAvailabilityStatusText } from '../helper.js';
import { LogistikRoutes } from '../routes.js';
import './logistik-component-audience-tag.js';

/**
 * @customElement logistik-component-product-card
 */
@customElement('logistik-component-product-card')
export class LogistikComponentProductCard extends ChemistryLitElement {
  override styles() {
    return [...super.styles(), { ':host': { display: 'flex', width: '100%', cursor: 'pointer' } }];
  }

  @property({ attribute: false }) public product = new LogistikProduct({});

  @state() private _urlParams = new RXController<string | undefined, string | undefined>(this, undefined);

  override connectedCallback(): void {
    super.connectedCallback();

    routingState.searchParams.pipe(distinctUntilChanged()).subscribe(sps => this._urlParams.next(sps.toString()));

    fromEvent(this, 'click').pipe(takeUntil(this.disconnected));
  }

  override render() {
    return html`
      <cr-link
        href="${LogistikRoutes.productItem}/${this.product._id}?${this._urlParams.value ?? ''}"
        ${css({ display: 'flex', flex: '1' })}
      >
        <cr-card ${css({ flex: '1' })} href="#">
          <cr-image
            disabled
            mode="width"
            .value=${this.product.imageID}
            ${css({
              width: `calc(100% + 2 * ${this.spacing.md})`,
              margin: `-${this.spacing.md} -${this.spacing.md} 0`,
            })}
          ></cr-image>
          <cr-flex space="md" ${css({ maxWidth: '350px' })}>
            <cr-flex> </cr-flex>
            <cr-flex space="sm">
              ${this.product instanceof AdvertisingMaterial ? this._audienceList() : nothing}
              <cr-flex horizontal space-between crossaxis-center>
                ${this.product instanceof LearningMaterial ? this._categoryTag(this.product.category) : nothing}
                ${g_getAvailabilityStatusText(this.product.availability)}
              </cr-flex>
              <cr-text bold>${this.product.title}</cr-text>
            </cr-flex>
          </cr-flex>
        </cr-card>
      </cr-link>
    `;
  }

  private _audienceList() {
    return html`
      <cr-flex horizontal wrap space="sm">
        ${((this.product as AdvertisingMaterial).targetAudiences ?? []).map(
          targetAudience =>
            html` <logistik-component-audience-tag .audience=${targetAudience}></logistik-component-audience-tag> `,
        )}
      </cr-flex>
    `;
  }

  private _categoryTag(tagText: string) {
    return html`
      <cr-flex
        ${css({
          padding: this.spacing.xs,
          borderRadius: '5px',
          background: this._getLabelColor((this.product as LearningMaterial).category),
        })}
      >
        <cr-text ${css({ fontSize: '0.8rem' })}>${tagText}</cr-text>
      </cr-flex>
    `;
  }

  private _getLabelColor(category: LogistikLearningMaterialCategory) {
    switch (category) {
      case LogistikLearningMaterialCategory.GERMAN:
        return '#f4d627';
      case LogistikLearningMaterialCategory.ENGLISH:
        return '#e28807';
      case LogistikLearningMaterialCategory.MATH:
        return '#006359';
      case LogistikLearningMaterialCategory.CHEMISTRY:
        return '#afca00';
      case LogistikLearningMaterialCategory.BIOLOGY:
        return '#c3e5f4';
      case LogistikLearningMaterialCategory.EXPERTISE:
        return '#613d1f';
    }
  }
}
